// Brand Campaign Detail page component for InfluenceHub MVP
// Shows read-only detailed information about a specific campaign for brand users
// Displays exactly what the brand posted (name, brief, deliverables, requirements, etc.)

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';
import { deliverableLabels } from '../lib/deliverables.js';

const BrandCampaignDetail = ({ campaignId, onNavigate }) => {
  const { user, profile } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        if (!user || !profile) {
          setError('Please log in to view campaign details');
          setLoading(false);
          return;
        }

        if (profile.userType !== 'brand') {
          setError('This page is only available for brands');
          setLoading(false);
          return;
        }

        if (!db || !campaignId) {
          setError('Invalid campaign ID');
          setLoading(false);
          return;
        }

        // Fetch campaign
        const campaignRef = doc(db, 'campaigns', campaignId);
        const campaignSnap = await getDoc(campaignRef);

        if (!campaignSnap.exists()) {
          setError('Campaign not found');
          setLoading(false);
          return;
        }

        const campaignData = campaignSnap.data();

        // Verify the brand owns this campaign
        if (campaignData.brandId !== user.uid) {
          setError('You do not have permission to view this campaign');
          setLoading(false);
          return;
        }

        // Fetch brand logo if brandId is available
        let logoUrl = null;
        if (campaignData.brandId) {
          try {
            const brandRef = doc(db, 'users', campaignData.brandId);
            const brandSnap = await getDoc(brandRef);
            if (brandSnap.exists()) {
              logoUrl = brandSnap.data().logoUrl || null;
            }
          } catch (logoError) {
            console.warn('Could not fetch brand logo:', logoError);
          }
        }

        setBrandLogo(logoUrl);
        
        // Check for deliverables and requirements in various possible field names
        // Some campaigns might have them as arrays, strings, or not at all
        let deliverables = null;
        if (campaignData.deliverables) {
          // If it's an array, use it directly
          if (Array.isArray(campaignData.deliverables)) {
            deliverables = campaignData.deliverables;
          } 
          // If it's a string, try to parse it or split by comma
          else if (typeof campaignData.deliverables === 'string') {
            deliverables = campaignData.deliverables.split(',').map(d => d.trim()).filter(d => d);
          }
        }
        
        let requirements = null;
        if (campaignData.requirements) {
          // If it's an array, use it directly
          if (Array.isArray(campaignData.requirements)) {
            requirements = campaignData.requirements;
          } 
          // If it's a string, try to parse it or split by comma
          else if (typeof campaignData.requirements === 'string') {
            requirements = campaignData.requirements.split(',').map(r => r.trim()).filter(r => r);
          }
        }
        
        setCampaign({
          id: campaignSnap.id,
          name: campaignData.title || campaignData.name || campaignData.campaignName || 'Untitled Campaign',
          brandName: campaignData.brandName || profile.companyName || null,
          description: campaignData.bio || campaignData.description || campaignData.brief || null,
          deliverables: deliverables,
          requirements: requirements,
          payout: campaignData.budgetUSD || campaignData.budget || (campaignData.payment_cents ? campaignData.payment_cents / 100 : null),
          budgetMin: campaignData.budgetMin || null,
          budgetMax: campaignData.budgetMax || null,
          deadline: campaignData.deadline || null,
          productLink: campaignData.productLink || null,
          brandWebsite: campaignData.brandWebsite || campaignData.website || null,
          collaborationType: campaignData.collaborationType || null,
          shipFrom: campaignData.shippingFromCountry || campaignData.shippingFrom || campaignData.shipFrom || null,
          createdAt: campaignData.createdAt || null,
        });
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError('Failed to load campaign details');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId, user, profile]);

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (e) {
      return '—';
    }
  };

  // Format payout helper
  const formatPayout = (payout) => {
    if (payout == null) return null;
    if (typeof payout === 'number') {
      return `$${payout.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    return payout;
  };

  // Format budget range
  const formatBudgetRange = () => {
    if (campaign?.budgetMin != null || campaign?.budgetMax != null) {
      const min = campaign.budgetMin != null ? `$${campaign.budgetMin.toLocaleString()}` : '—';
      const max = campaign.budgetMax != null ? `$${campaign.budgetMax.toLocaleString()}` : '—';
      return `${min}–${max}`;
    }
    return null;
  };

  // Get initials for brand logo fallback
  const getInitials = (name) => {
    if (!name) return 'BR';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
              <div className="h-40 w-full bg-gray-100 rounded animate-pulse" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => onNavigate('my-campaigns')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Back to My Campaigns
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!campaign) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onNavigate={onNavigate} />
      <div className="flex">
        <Sidebar onNavigate={onNavigate} />
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Brand Logo */}
                <div className="h-12 w-12 rounded-xl bg-gray-200 flex items-center justify-center overflow-hidden relative flex-shrink-0">
                  {brandLogo ? (
                    <img 
                      src={brandLogo} 
                      alt={campaign.brandName || 'Brand'} 
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        if (e.target) {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement?.querySelector('.logo-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div className={`logo-fallback absolute inset-0 flex items-center justify-center ${brandLogo ? 'hidden' : ''}`}>
                    <span className="text-lg font-semibold text-gray-600">
                      {getInitials(campaign.brandName)}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">{campaign.brandName || 'Brand'}</div>
                  <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
                  <div className="text-xs text-gray-500 mt-1">
                    Created {formatDate(campaign.createdAt)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onNavigate('my-campaigns')}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              </div>
            </div>

            {/* Description/Brief */}
            {campaign.description && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-medium text-gray-500 mb-3">Campaign Brief</div>
                <p className="text-gray-800 whitespace-pre-line leading-relaxed">
                  {campaign.description}
                </p>
              </div>
            )}

            {/* Deliverables and Requirements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Deliverables */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-medium text-gray-500 mb-3">Deliverables</div>
                <div className="flex flex-wrap gap-2">
                  {campaign.deliverables && Array.isArray(campaign.deliverables) && campaign.deliverables.length > 0 ? (
                    deliverableLabels(campaign.deliverables).map((label, i) => (
                      <span 
                        key={i} 
                        className="inline-flex items-center rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700"
                      >
                        {label}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">No deliverables specified</span>
                  )}
                </div>
              </div>

              {/* Requirements */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-medium text-gray-500 mb-3">Requirements</div>
                <div className="flex flex-wrap gap-2">
                  {campaign.requirements && Array.isArray(campaign.requirements) && campaign.requirements.length > 0 ? (
                    deliverableLabels(campaign.requirements).map((label, i) => (
                      <span 
                        key={i} 
                        className="inline-flex items-center rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700"
                      >
                        {label}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">No requirements specified</span>
                  )}
                </div>
              </div>
            </div>

            {/* Meta Information Grid */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Payout</div>
                  <div className="font-medium text-gray-900">
                    {campaign.payout ? formatPayout(campaign.payout) : formatBudgetRange() || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Content Submission Deadline</div>
                  <div className="font-medium text-gray-900">
                    {typeof campaign.deadline === 'string' 
                      ? campaign.deadline 
                      : formatDate(campaign.deadline)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Collaboration Type</div>
                  <div className="font-medium text-gray-900">{campaign.collaborationType || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Ships From</div>
                  <div className="font-medium text-gray-900">{campaign.shipFrom || '—'}</div>
                </div>
                {campaign.productLink && (
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Product Link</div>
                    <div className="font-medium text-gray-900 break-all">
                      <a 
                        href={campaign.productLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {campaign.productLink}
                      </a>
                    </div>
                  </div>
                )}
                {campaign.brandWebsite && (
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Brand Website</div>
                    <div className="font-medium text-gray-900 break-all">
                      <a 
                        href={campaign.brandWebsite} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {campaign.brandWebsite}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BrandCampaignDetail;

